import { HTTP_INTERCEPTORS, HttpBackend, HttpClient } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { AuthClientConfig, AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { OAuthModule } from 'angular-oauth2-oidc';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';

import { ZitiWasmModule } from '@netfoundry-ui/shared/ziti-wasm';
import { FeatureActionButtonModule } from '@netfoundry-ui/feature/action-button';
import { FeatureAutoFabricModule } from '@netfoundry-ui/feature/auto-fabric';
import { FeatureCreditCardWarningModule } from '@netfoundry-ui/feature/credit-card-warning';
import { FeatureDialLogsTimelineModule } from '@netfoundry-ui/feature/dial-logs-timeline';
import { AlertDetailDrawerModule } from '@netfoundry-ui/feature/alert-detail-drawer';
import { FeatureSideFilterModule } from '@netfoundry-ui/feature/side-filter';
import { FeatureViewsTemplateModule } from '@netfoundry-ui/feature/views/template';
import { FeatureAppSwitcherModule } from '@netfoundry-ui/feature/app-switcher';
import { FeatureAssistantChatModule } from '@netfoundry-ui/feature/assistant-chat';
import {
    ControllerLoginService,
    NETWORK_SERVICE,
    NetworkServiceV2,
    SharedApiv2Module,
    TEMPLATE_SEARCH_SERVICE,
    V8TemplateSearchService,
    ZitiApiInterceptor,
    ZitiControllerService,
} from '@netfoundry-ui/shared/apiv2';

import {
  Auth0Service,
  applyRuntimeAuthConfig,
  AUTHENTICATION, AuthorizationService,
  IAM_CONFIGURATION,
  IamConfig, IamService,
  LOGIN_SERVICE,
  LoginService,
  SharedAuthorizationModule,
} from '@netfoundry-ui/shared/authorization';
import { SharedBannerMessageModule } from '@netfoundry-ui/shared/banner-message';
import { SharedClientSyncsModule } from '@netfoundry-ui/shared/client-syncs';
import { SharedElasticsearchModule } from '@netfoundry-ui/shared/elasticsearch';
import { SharedSideBarModule } from '@netfoundry-ui/feature/side-bar';
import { SharedOverlayModule } from '@netfoundry-ui/feature/overlay';
import {
    _VERSION,
    ADVANCED_SERVICE_DIALOG,
    Attribute,
    CONFIG_SERVICE_DIALOG,
    EDGE_ROUTER_UPGRADE_DIALOG,
    ENDPOINT_DIALOG,
    EndpointV2,
    ENROLLMENT_DIALOG,
    ENVIRONMENT,
    EVENT_FIELDS,
    MultipleResourceRoutes,
    NETWORK_RESTART_DIALOG,
    NETWORK_UPGRADE_DIALOG,
    NetworkV2,
    ResourceRoute,
    ROUTER_DIALOG,
    SERVICE_DIALOG,
} from '@netfoundry-ui/shared/model';
import { SharedNotificationModule } from '@netfoundry-ui/shared/notification';
import {
  ApiService,
  BrandingModeService,
  ExtensionsNoopService as NfExtensionsNoopService,
  HTTP_CLIENT,
  MenuService,
  SharedServicesModule,
  ZendeskWidgetService,
} from '@netfoundry-ui/shared/services';
import { ApiServiceTemplateStorage, ServiceTemplateStorage } from '@netfoundry-ui/shared/service-templates';
// SubFeature
import { HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgArrayPipesModule } from 'ngx-pipes';
import { NgxStripeModule } from 'ngx-stripe';
import { TimeagoModule } from 'ngx-timeago';
// NF Feature
// Main Feature
import { NgxHateoasClientConfigurationService, NgxHateoasClientModule } from '@lagoshny/ngx-hateoas-client';

import {
    FeatureFormZitiEdgeRouterFormModule,
    ZitiEdgeRouterComponent,
} from '@netfoundry-ui/feature/form/ziti-edge-router-form';
import { FeatureFormZitiIpsecTunnelerFormModule } from '@netfoundry-ui/feature/form/ziti-ipsec-tunneler-form';
import { PageIpsecTunnelerExtensionModule } from '@netfoundry-ui/page/ipsec-tunneler-extension';
import { LottieComponent, provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import { UiPipesModule } from '@netfoundry-ui/ui/pipes';
import { EnrollmentInstructionsComponent, FeatureGettingStartedModule } from '@netfoundry-ui/feature/getting-started';
import {
    EdgeRouterUpgradeModalComponent,
    FeatureNetworkUpgradeModule,
    NetworkRestartModalComponent,
    NetworkUpgradeModalComponent,
} from '@netfoundry-ui/feature/network-upgrade';

import { FeatureNavigatorModule } from '@netfoundry-ui/feature/navigator';
import {
    DEACTIVATE_GUARD,
    DeactivateGuardService,
    EDGE_ROUTER_EXTENSION_SERVICE,
    EDGE_ROUTER_POLICY_EXTENSION_SERVICE,
    SERVICE_EDGE_ROUTER_POLICY_EXTENSION_SERVICE,
    GrowlerModule,
    IDENTITY_EXTENSION_SERVICE,
    OpenZitiConsoleLibModule,
    SERVICE_EXTENSION_SERVICE,
    SERVICE_POLICY_EXTENSION_SERVICE,
    SETTINGS_SERVICE,
    SettingsServiceClass,
    SettingsService,
    SHAREDZ_EXTENSION,
    ZAC_LOGIN_SERVICE,
    ZITI_DATA_SERVICE,
    ZITI_DOMAIN_CONTROLLER,
    ZITI_NAVIGATOR,
    ZITI_TAB_OVERRIDES,
    ZITI_URLS,
    ZitiControllerDataService,
    DEFAULT_APP_CONFIG,
    ManagementPermissionsService,
} from 'ziti-console-lib';
import { ZitiEndpointFormComponent } from '@netfoundry-ui/feature/form/ziti-endpoint-form';
import { PlatformServiceFormComponent } from '@netfoundry-ui/feature/form/platform-service-form';
import { AdvancedPlatformServiceFormComponent } from '@netfoundry-ui/feature/form/advanced-platform-service-form';
import { AdvancedServiceConfigFormComponent } from '@netfoundry-ui/feature/form/advanced-service-config-form';
import { CommonModule } from '@angular/common';
import { ClickOutsideModule } from 'ng-click-outside';
import { FormsModule } from '@angular/forms';
import { EdgeRoutersExtensionService } from '@netfoundry-ui/page/edge-routers-extension';
import { IdentitiesExtensionService } from '@netfoundry-ui/page/identities-extension';
import { PageServicePoliciesExtensionModule } from '@netfoundry-ui/page/service-policies-extension';
import { ServicesExtensionService } from '@netfoundry-ui/page/services-extension';
import { ServicePoliciesExtensionService } from '@netfoundry-ui/page/service-policies-extension';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from '../environments/environment';
import { VERSION } from '../environments/version';
import { OPEN_ZITI_NAVIGATOR } from './app-nav.constants';
import { AppComponent } from './app.component';
import { EntityRouteReuseStrategy } from './entity-route-reuse.strategy';
import { AppRoutingModule } from './app-routing.module';
import { URLS } from './app-urls.constants';
import { InitializerService } from './initializer.service';
import { ServiceTemplatesTabInterceptorService } from './service-templates-tab.interceptor';
import { RecentAlertsComponent, SharedRecentAlertsModule } from '@netfoundry-ui/shared/recent-alerts';
import { CLOUD_ZITI_EVENT_FIELDS } from '../models/cloud-ziti-event-fields';
import {UiLoaderModule} from "@netfoundry-ui/ui/loader";
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { DEFAULT_APP_CONFIG_PROP } from '../models/default-app-config';

function initializerFactory(
    authClientConfig: AuthClientConfig,
    iamConfig: IamConfig,
    http: HttpClient,
    handler: HttpBackend,
    authService: AuthorizationService,
    iamService: IamService,
    brandingService: BrandingModeService,
    apiService: ApiService
) {
    return InitializerService.initialize(authClientConfig, iamConfig, http, handler, authService, iamService, brandingService, apiService);
}

export function playerFactory() {
    return import('lottie-web');
}

@NgModule({
    declarations: [AppComponent],
    providers: [
        // Rebuilds an entity page marked rebuildOnIdChange when its :id changes; those forms load their entity only
        // once.
        { provide: RouteReuseStrategy, useClass: EntityRouteReuseStrategy },
        providePrimeNG({
            theme: {
                preset: Aura
            }
        }),
        { provide: ENVIRONMENT, useValue: environment },
        { provide: _VERSION, useValue: VERSION },
        { provide: LOGIN_SERVICE, useExisting: LoginService },
        { provide: HTTP_CLIENT, useClass: HttpClient },
        { provide: IAM_CONFIGURATION, useValue: InitializerService.identityConfig },
        { provide: AUTHENTICATION, useExisting: Auth0Service },
        {
            provide: HIGHLIGHT_OPTIONS,
            useValue: { fullLibraryLoader: () => import('highlight.js'), themePath: 'assets/styles/monokai.css' },
        },
        provideAppInitializer(() => {
        const authClientConfig = inject(AuthClientConfig);
        const iamConfig = inject(IAM_CONFIGURATION);
        const httpClient = inject(HttpClient);
        const handler = inject(HttpBackend);
        const authService = inject(AuthorizationService);
        const iamService = inject(IamService);
        const brandingService = inject(BrandingModeService);
        const apiService = inject(ApiService);
        // Configure Auth0 synchronously with the compiled baseline so AuthClientConfig is never
        // unset while the runtime override is in flight (other APP_INITIALIZERs run concurrently).
        // The initializer below re-sets it with the resolved domain + per-origin redirect_uri.
        authClientConfig.set(InitializerService.authConfig);
        // Overlay the per-deploy auth-runtime.json (Classic <-> ACUL), then (re)configure Auth0.
        return applyRuntimeAuthConfig(environment, handler).then(() => {
            InitializerService.authConfig.domain = environment.authconfig.domain;
            const initializerFn = (initializerFactory)(authClientConfig, iamConfig, httpClient, handler, authService, iamService, brandingService, apiService);
            return initializerFn();
        });
      }),
        provideAppInitializer(() => {
            const settings = inject(SETTINGS_SERVICE) as SettingsServiceClass;
            inject(ManagementPermissionsService);
            return settings.init();
        }),
        provideAppInitializer(() => {
            const zendesk = inject(ZendeskWidgetService);
            return zendesk.initZendesk('en');
        }),
        provideAppInitializer(() => {
        const initializerFn = (() => InitializerService.initLogRocket)();
        return initializerFn();
      }),
        { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
        { provide: NETWORK_SERVICE, useClass: NetworkServiceV2 },
        { provide: ENDPOINT_DIALOG, useValue: ZitiEndpointFormComponent },
        { provide: NETWORK_RESTART_DIALOG, useValue: NetworkRestartModalComponent },
        { provide: NETWORK_UPGRADE_DIALOG, useValue: NetworkUpgradeModalComponent },
        { provide: ENROLLMENT_DIALOG, useValue: EnrollmentInstructionsComponent },
        { provide: ROUTER_DIALOG, useValue: ZitiEdgeRouterComponent },
        { provide: SERVICE_DIALOG, useValue: PlatformServiceFormComponent },
        { provide: ADVANCED_SERVICE_DIALOG, useValue: AdvancedPlatformServiceFormComponent },
        { provide: CONFIG_SERVICE_DIALOG, useValue: AdvancedServiceConfigFormComponent },
        { provide: ROUTER_DIALOG, useValue: ZitiEdgeRouterComponent },
        { provide: ZITI_DOMAIN_CONTROLLER, useClass: ZitiControllerService },
        { provide: ZITI_NAVIGATOR, useValue: OPEN_ZITI_NAVIGATOR },
        { provide: ZITI_URLS, useValue: URLS },
        { provide: ZITI_TAB_OVERRIDES, useClass: ServiceTemplatesTabInterceptorService },
        { provide: HTTP_INTERCEPTORS, useClass: ZitiApiInterceptor, multi: true },
        { provide: SHAREDZ_EXTENSION, useClass: NfExtensionsNoopService },
        { provide: EDGE_ROUTER_UPGRADE_DIALOG, useValue: EdgeRouterUpgradeModalComponent },
        { provide: DEACTIVATE_GUARD, useClass: DeactivateGuardService },
        { provide: ZITI_DATA_SERVICE, useClass: ZitiControllerDataService },
        { provide: SETTINGS_SERVICE, useClass: SettingsService },
        { provide: ZAC_LOGIN_SERVICE, useClass: ControllerLoginService },
        { provide: EDGE_ROUTER_EXTENSION_SERVICE, useClass: EdgeRoutersExtensionService },
        { provide: DEFAULT_APP_CONFIG, useValue: DEFAULT_APP_CONFIG_PROP},
        { provide: IDENTITY_EXTENSION_SERVICE, useClass: IdentitiesExtensionService },
        { provide: SERVICE_EXTENSION_SERVICE, useClass: ServicesExtensionService },
        { provide: EVENT_FIELDS, useValue: CLOUD_ZITI_EVENT_FIELDS },
        { provide: TEMPLATE_SEARCH_SERVICE, useClass: V8TemplateSearchService },
        { provide: SERVICE_POLICY_EXTENSION_SERVICE, useClass: ServicePoliciesExtensionService },
        { provide: EDGE_ROUTER_POLICY_EXTENSION_SERVICE, useClass: NfExtensionsNoopService },
        { provide: SERVICE_EDGE_ROUTER_POLICY_EXTENSION_SERVICE, useClass: NfExtensionsNoopService },
        { provide: ServiceTemplateStorage, useClass: ApiServiceTemplateStorage },
        provideCharts(withDefaultRegisterables()),
        provideLottieOptions({
            player: playerFactory
        }),
    ],
    imports: [
        CommonModule,
        ZitiWasmModule,
        ClickOutsideModule,
        FormsModule,
        UiPipesModule,
        FeatureAppSwitcherModule,
        FeatureViewsTemplateModule,
        PageServicePoliciesExtensionModule,
        FeatureAutoFabricModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        BrowserModule,
        FeatureActionButtonModule,
        FeatureCreditCardWarningModule,
        FeatureDialLogsTimelineModule,
        AlertDetailDrawerModule,
        LoggerModule.forRoot({ level: NgxLoggerLevel.DEBUG, serverLogLevel: NgxLoggerLevel.ERROR }),
        NgArrayPipesModule,
        NgxHateoasClientModule.forRoot(),
        NgxPaginationModule,
        NgxStripeModule.forRoot(environment.billingConfig.stripe),
        SharedApiv2Module.forRoot(),
        SharedAuthorizationModule.forRoot(InitializerService.authorizationConfig),
        SharedBannerMessageModule,
        SharedClientSyncsModule.forRoot(InitializerService.clientSyncsConfig),
        SharedElasticsearchModule.forRoot(InitializerService.elasticConfig),
        SharedNotificationModule.forRoot(InitializerService.notificationConfig),
        TimeagoModule.forRoot(),
        RouterModule,
        UiPipesModule,
        FeatureSideFilterModule,
        SharedServicesModule,
        AuthModule.forRoot(),
        OAuthModule.forRoot(),
        FeatureFormZitiEdgeRouterFormModule,
        FeatureFormZitiIpsecTunnelerFormModule,
        PageIpsecTunnelerExtensionModule,
        FeatureGettingStartedModule,
        LottieComponent,
        FeatureNetworkUpgradeModule,
        FeatureNavigatorModule,
        SharedSideBarModule,
        SharedOverlayModule,
        GrowlerModule,
        OpenZitiConsoleLibModule,
        MatTooltipModule,
        SharedRecentAlertsModule,
        FeatureAssistantChatModule,
        UiLoaderModule
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent],
})
export class AppModule {
    constructor(hateoasConfig: NgxHateoasClientConfigurationService) {
        const v2ResourceRoute: ResourceRoute = {
            rootUrl: environment.v2apiUrl,
        };
        const v3ResourceRoute: ResourceRoute = {
            rootUrl: environment.v3apiUrl,
        };
        const customerRoute: ResourceRoute = {
            rootUrl: environment.customerURL,
        }
        const frontdoorRoute: ResourceRoute = {
            rootUrl: environment.frontdoorURL,
        }

        const integrationsRoute: ResourceRoute = {
            rootUrl: environment.integrationsManagementUrl
        }


        const multipleRoutes: MultipleResourceRoutes = {
            defaultRoute: v3ResourceRoute,
            networkResourceRoute: v3ResourceRoute,
            customerResourceRoute: customerRoute,
            frontdoorResourceRoute: frontdoorRoute,
            integrationsResourceRoute: integrationsRoute,
        };

        hateoasConfig.configure({
            http: multipleRoutes,
            logs: { verboseLogs: false },
            useTypes: {
                resources: [NetworkV2, Attribute, EndpointV2],
            },
            halFormat: {
                collections: {
                    embeddedOptional: true,
                },
            },
            cache: { enabled: true, lifeTime: 200 },
            isProduction: environment.production,
            pagination: {
                defaultPage: {
                    page: 0,
                    size: environment.pageSize ?? 50,
                },
            },
        });
    }
}
